import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@material-ui/core";
import {
  CloudDownloadOutlined,
  DateRangeOutlined,
  LocationOnOutlined,
  RefreshOutlined,
  TrendingDownOutlined,
  TrendingFlatOutlined,
  TrendingUpOutlined,
  WarningOutlined,
} from "@material-ui/icons";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MapBoxMap from "../../../components/MapBox";
import { loadDashboardOverview } from "../../../services/dashboardOverviewService";
import {
  createStaticRanges,
  DateRangePicker,
  defaultInputRanges,
} from "react-date-range";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { enUS, es } from "date-fns/locale";
import {
  formatCurrency,
  formatDate,
  formatNumber,
} from "../../../utils/localization";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const Hero = styled(Card)`
  border-radius: 26px;
  color: #fff;
  background: linear-gradient(135deg, #0f2948, #1d4e89 58%, #67b5de);
  box-shadow: 0 22px 48px rgba(15, 41, 72, 0.2);
`;

const Surface = styled(Card)`
  height: 100%;
  border-radius: 22px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.06);
`;

const CompactSurface = styled(Surface)`
  height: auto;
  min-height: 0;
`;

const RecommendationsViewport = styled.div`
  max-height: min(52vh, 520px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 8px 4px 2px;

  scrollbar-width: thin;
  scrollbar-color: #94a3b8 transparent;
`;

const Metric = styled(Surface)`
  background: linear-gradient(180deg, #fff, #f8fbff);
`;

const Eyebrow = styled(Typography)`
  && {
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }
`;

const EmptyState = styled(Box)`
  padding: 34px;
  text-align: center;
  color: #64748b;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
`;

const MapDialogHeader = styled.div`
  padding: 22px 24px 18px;
  color: #fff;
  background: linear-gradient(110deg, #173f70 0%, #245d96 62%, #65b6de 100%);
`;

const MapDialogFooter = styled(DialogActions)`
  && {
    padding: 14px 18px;
    background: #f4f7fb;
    border-top: 1px solid #e2e8f0;
  }
`;

const currency = { format: (value) => formatCurrency(value) };
const integer = {
  format: (value) => formatNumber(value, { maximumFractionDigits: 0 }),
};
const shortDate = {
  format: (value) =>
    formatDate(value, { day: "2-digit", month: "short", dateStyle: undefined }),
};

const toIsoDate = (date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const defaultRange = () => {
  const today = new Date();
  return {
    start: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    end: toIsoDate(today),
  };
};

const buildDashboardStaticRanges = (t) =>
  createStaticRanges([
    {
      label: t("dateRanges.today"),
      range: () => ({ startDate: new Date(), endDate: new Date() }),
    },
    {
      label: t("dateRanges.yesterday"),
      range: () => ({
        startDate: addDays(new Date(), -1),
        endDate: addDays(new Date(), -1),
      }),
    },
    {
      label: t("dateRanges.thisWeek"),
      range: () => ({
        startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
        endDate: new Date(),
      }),
    },
    {
      label: t("dateRanges.lastWeek"),
      range: () => ({
        startDate: startOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
        endDate: endOfWeek(addDays(new Date(), -7), { weekStartsOn: 1 }),
      }),
    },
    {
      label: t("dateRanges.thisMonth"),
      range: () => ({
        startDate: startOfMonth(new Date()),
        endDate: new Date(),
      }),
    },
    {
      label: t("dateRanges.lastMonth"),
      range: () => ({
        startDate: startOfMonth(addDays(startOfMonth(new Date()), -1)),
        endDate: endOfMonth(addDays(startOfMonth(new Date()), -1)),
      }),
    },
  ]);

const percent = (value, t) =>
  value === null || value === undefined
    ? t("dashboard.noComparison")
    : `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
const formatName = (value, t) => value || t("dashboard.unnamed");

function Variation({ value, label = "vs. período comparable" }) {
  const { t } = useTranslation();
  const Icon =
    value === null || value === undefined || value === 0
      ? TrendingFlatOutlined
      : value > 0
      ? TrendingUpOutlined
      : TrendingDownOutlined;
  const color =
    value === null || value === undefined || value === 0
      ? "#64748b"
      : value > 0
      ? "#047857"
      : "#b91c1c";
  return (
    <Box display="flex" alignItems="center" mt={1} style={{ color }}>
      <Icon fontSize="small" />
      <Typography variant="caption" style={{ marginLeft: 5 }}>
        {percent(value, t)}{" "}
        {label === "vs. período comparable"
          ? t("dashboard.vsComparable")
          : label}
      </Typography>
    </Box>
  );
}

function MetricCard({ label, value, variation, helper }) {
  return (
    <Metric>
      <CardContent>
        <Eyebrow>{label}</Eyebrow>
        <Typography
          variant="h4"
          style={{ color: "#10213a", fontWeight: 800, marginTop: 10 }}
        >
          {value}
        </Typography>
        {variation !== undefined && <Variation value={variation} />}
        {helper && (
          <Typography variant="caption" color="textSecondary">
            {helper}
          </Typography>
        )}
      </CardContent>
    </Metric>
  );
}

function SectionHeading({ eyebrow, title, description, action }) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      mb={3}
      flexWrap="wrap"
    >
      <Box pr={2}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Typography variant="h5" style={{ color: "#10213a", fontWeight: 800 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

function DashboardSummary() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const initialRange = useMemo(defaultRange, []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [dateAnchor, setDateAnchor] = useState(null);
  const [draftRange, setDraftRange] = useState([
    {
      startDate: new Date(`${initialRange.start}T12:00:00`),
      endDate: new Date(`${initialRange.end}T12:00:00`),
      key: "selection",
    },
  ]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [mapCoordinates, setMapCoordinates] = useState(null);
  const dashboardStaticRanges = useMemo(() => buildDashboardStaticRanges(t), [
    t,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await loadDashboardOverview(
        appliedRange.start,
        appliedRange.end
      );
      setOverview(response.data);
      setPage(0);
    } catch (requestError) {
      const responseData = requestError?.response?.data;
      setError(
        typeof responseData === "string"
          ? responseData
          : responseData?.message || t("dashboard.loadError")
      );
    } finally {
      setLoading(false);
    }
  }, [appliedRange, t]);

  useEffect(() => {
    load();
  }, [load]);

  const applyRange = () => {
    const nextRange = {
      start: toIsoDate(draftRange[0].startDate),
      end: toIsoDate(draftRange[0].endDate),
    };
    setStartDate(nextRange.start);
    setEndDate(nextRange.end);
    setAppliedRange(nextRange);
    setDateAnchor(null);
  };

  const exportCsv = () => {
    if (!overview) return;
    const rows = [
      [
        t("dashboard.accessPoint"),
        t("dashboard.validViews"),
        t("dashboard.netEarningsDop"),
        t("dashboard.earningsPerThousand"),
        t("dashboard.liveConnections"),
      ],
      ...overview.accessPoints.map((ap) => [
        ap.name,
        ap.views,
        ap.netEarnings,
        ap.earningsPerThousandViews,
        ap.liveConnections,
      ]),
    ];
    const content = rows
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob(["\ufeff", content], { type: "text/csv;charset=utf-8" })
    );
    link.download = `dashboard-ap-${appliedRange.start}-${appliedRange.end}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const lineData = overview
    ? {
        labels: overview.dailySeries.map((point) =>
          shortDate.format(new Date(`${point.date}T12:00:00`))
        ),
        datasets: [
          {
            label: t("dashboard.netEarnings"),
            data: overview.dailySeries.map((point) => point.netEarnings),
            borderColor: "#1d4e89",
            backgroundColor: "rgba(29,78,137,.12)",
            fill: true,
            yAxisID: "earnings",
            lineTension: 0.25,
          },
          {
            label: t("dashboard.validViews"),
            data: overview.dailySeries.map((point) => point.views),
            borderColor: "#10b981",
            backgroundColor: "transparent",
            yAxisID: "views",
            lineTension: 0.25,
          },
        ],
      }
    : null;

  const lineOptions = {
    maintainAspectRatio: false,
    legend: { position: "bottom" },
    tooltips: { mode: "index", intersect: false },
    scales: {
      yAxes: [
        {
          id: "earnings",
          position: "left",
          ticks: {
            beginAtZero: true,
            callback: (value) => currency.format(value),
          },
        },
        {
          id: "views",
          position: "right",
          ticks: { beginAtZero: true },
          gridLines: { display: false },
        },
      ],
    },
  };

  const apChartData = overview
    ? {
        labels: overview.accessPoints
          .slice(0, 8)
          .map((ap) => formatName(ap.name, t)),
        datasets: [
          {
            label: t("dashboard.netEarnings"),
            data: overview.accessPoints.slice(0, 8).map((ap) => ap.netEarnings),
            backgroundColor: "#1d4e89",
          },
        ],
      }
    : null;

  const formatChartData = overview
    ? {
        labels: overview.formats.map((item) => item.name),
        datasets: [
          {
            data: overview.formats.map((item) => item.netEarnings),
            backgroundColor: ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"],
          },
        ],
      }
    : null;

  const session = JSON.parse(sessionStorage.getItem("token_store") || "{}");

  return (
    <>
      <Helmet title={t("dashboard.smartTitle")} />
      <Hero>
        <CardContent style={{ padding: 30 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="overline" style={{ opacity: 0.78 }}>
                {t("dashboard.executive")}
              </Typography>
              <Typography variant="h3" style={{ fontWeight: 800 }}>
                {t("dashboard.hello", {
                  name: session.name || t("dashboard.operator"),
                })}
              </Typography>
              <Typography
                variant="body1"
                style={{ opacity: 0.9, marginTop: 8 }}
              >
                {t("dashboard.description")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                display="flex"
                justifyContent="flex-end"
                flexWrap="wrap"
                style={{ gap: 10 }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<DateRangeOutlined />}
                  onClick={(event) => setDateAnchor(event.currentTarget)}
                  style={{ borderRadius: 12, textTransform: "none" }}
                >
                  {t("dashboard.dateRange")}
                </Button>
                <Typography
                  variant="caption"
                  style={{ width: "100%", textAlign: "right", opacity: 0.9 }}
                >
                  {startDate} – {endDate}
                </Typography>
                <Popover
                  open={Boolean(dateAnchor)}
                  anchorEl={dateAnchor}
                  onClose={() => setDateAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    style: { borderRadius: 14, overflow: "hidden" },
                  }}
                >
                  <Box
                    style={{
                      maxWidth: "calc(100vw - 24px)",
                      overflowX: "auto",
                    }}
                  >
                    <DateRangePicker
                      ranges={draftRange}
                      onChange={(item) => setDraftRange([item.selection])}
                      staticRanges={dashboardStaticRanges}
                      inputRanges={defaultInputRanges}
                      months={1}
                      direction="horizontal"
                      locale={i18n.resolvedLanguage === "en" ? enUS : es}
                      maxDate={new Date()}
                      moveRangeOnFirstSelection={false}
                      showSelectionPreview
                    />
                    <Box p={1.5} pt={0}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={applyRange}
                        style={{ borderRadius: 10, textTransform: "none" }}
                      >
                        {t("common.apply")}
                      </Button>
                    </Box>
                  </Box>
                </Popover>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Hero>

      {error && (
        <Paper
          style={{
            marginTop: 22,
            padding: 18,
            borderLeft: "4px solid #b91c1c",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography style={{ color: "#991b1b" }}>
              {String(error)}
            </Typography>
            <Button startIcon={<RefreshOutlined />} onClick={load}>
              {t("dashboard.retry")}
            </Button>
          </Box>
        </Paper>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress aria-label={t("dashboard.loading")} />
        </Box>
      )}

      {!loading && overview && (
        <>
          <Box
            mt={3}
            mb={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="textSecondary">
              {t("dashboard.comparablePeriod")}:{" "}
              {overview.comparisonPeriod.startingDate} –{" "}
              {overview.comparisonPeriod.endingDate}
            </Typography>
            <Button startIcon={<CloudDownloadOutlined />} onClick={exportCsv}>
              {t("dashboard.exportCsv")}
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label={t("dashboard.payableNetEarnings")}
                value={currency.format(overview.totals.netEarnings)}
                variation={overview.variations.netEarnings}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label={t("dashboard.validViews")}
                value={integer.format(overview.totals.views)}
                variation={overview.variations.views}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label={t("dashboard.earningsPerThousand")}
                value={currency.format(
                  overview.totals.earningsPerThousandViews
                )}
                variation={overview.variations.earningsPerThousandViews}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                label={t("dashboard.activeAccessPoints")}
                value={`${overview.network.activeAccessPoints} / ${overview.network.registeredAccessPoints}`}
                helper={t("dashboard.inactiveAccessPoints", {
                  count: overview.network.inactiveAccessPoints,
                })}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12} md={8}>
              <Surface>
                <CardContent>
                  <SectionHeading
                    eyebrow={t("dashboard.trend")}
                    title={t("dashboard.dailyEvolution")}
                    description={t("dashboard.dailyEvolutionDescription")}
                  />
                  {overview.totals.views === 0 ? (
                    <EmptyState>
                      {t("dashboard.noMonetizedActivity")}
                    </EmptyState>
                  ) : (
                    <Box height={330}>
                      <Line data={lineData} options={lineOptions} />
                    </Box>
                  )}
                </CardContent>
              </Surface>
            </Grid>
            <Grid item xs={12} md={4}>
              <Surface>
                <CardContent>
                  <SectionHeading
                    eyebrow={t("dashboard.projection")}
                    title={t("dashboard.monthClose")}
                    description={
                      overview.projection.estimate
                        ? t("dashboard.estimatedProjection")
                        : t("dashboard.selectedPeriodResult")
                    }
                  />
                  <Typography
                    variant="h3"
                    style={{ fontWeight: 800, color: "#10213a" }}
                  >
                    {currency.format(
                      overview.projection.projectedMonthNetEarnings
                    )}
                  </Typography>
                  <Variation
                    value={overview.projection.variationAgainstPreviousMonth}
                    label={t("dashboard.vsPreviousMonth")}
                  />
                  <Divider style={{ margin: "22px 0" }} />
                  <Typography variant="body2" color="textSecondary">
                    {t("dashboard.previousMonth")}
                  </Typography>
                  <Typography variant="h6">
                    {currency.format(
                      overview.projection.previousMonthNetEarnings
                    )}
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="body2">
                      {t("dashboard.liveConnectionsNow")}:{" "}
                      <strong>{overview.network.liveConnections}</strong>
                    </Typography>
                  </Box>
                </CardContent>
              </Surface>
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12} md={7}>
              <Surface>
                <CardContent>
                  <SectionHeading
                    eyebrow={t("dashboard.formats")}
                    title={t("dashboard.formatPerformance")}
                    description={t("dashboard.formatPerformanceDescription")}
                  />
                  <TableContainer>
                    <Table
                      size="small"
                      aria-label={t("dashboard.formatPerformance")}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("dashboard.format")}</TableCell>
                          <TableCell align="right">
                            {t("dashboard.views")}
                          </TableCell>
                          <TableCell align="right">
                            {t("dashboard.netEarnings")}
                          </TableCell>
                          <TableCell align="right">
                            {t("dashboard.participation")}
                          </TableCell>
                          <TableCell align="right">
                            {t("dashboard.variation")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {overview.formats.map((item) => (
                          <TableRow key={item.mediaType}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell align="right">
                              {integer.format(item.views)}
                            </TableCell>
                            <TableCell align="right">
                              {currency.format(item.netEarnings)}
                            </TableCell>
                            <TableCell align="right">
                              {item.participation.toFixed(1)}%
                            </TableCell>
                            <TableCell align="right">
                              <span
                                style={{
                                  color:
                                    item.earningsVariation < 0
                                      ? "#b91c1c"
                                      : "#047857",
                                }}
                              >
                                {percent(item.earningsVariation, t)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Surface>
            </Grid>
            <Grid item xs={12} md={5}>
              <Surface>
                <CardContent>
                  <SectionHeading
                    eyebrow={t("dashboard.distribution")}
                    title={t("dashboard.earningsByFormat")}
                  />
                  {overview.totals.netEarnings === 0 ? (
                    <EmptyState>{t("dashboard.noEarnings")}</EmptyState>
                  ) : (
                    <Box height={280}>
                      <Doughnut
                        data={formatChartData}
                        options={{
                          maintainAspectRatio: false,
                          legend: { position: "bottom" },
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Surface>
            </Grid>
          </Grid>

          <Grid container spacing={3} style={{ marginTop: 4 }}>
            <Grid item xs={12} lg={8}>
              <Surface>
                <CardContent>
                  <SectionHeading
                    eyebrow={t("dashboard.devices")}
                    title={t("dashboard.deviceEarnings")}
                    description={t("dashboard.deviceEarningsDescription")}
                  />
                  {overview.accessPoints.length === 0 ? (
                    <EmptyState>{t("dashboard.noAccessPointViews")}</EmptyState>
                  ) : (
                    <>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        style={{ borderRadius: 16, overflow: "hidden" }}
                      >
                        <Table aria-label={t("dashboard.deviceEarnings")}>
                          <TableHead style={{ background: "#f6f9fd" }}>
                            <TableRow>
                              <TableCell>{t("dashboard.name")}</TableCell>
                              <TableCell align="right">
                                {t("dashboard.quantity")}
                              </TableCell>
                              <TableCell align="right">
                                {t("dashboard.payment")}
                              </TableCell>
                              <TableCell align="right">
                                {t("dashboard.liveConnections")}
                              </TableCell>
                              <TableCell align="center">
                                {t("dashboard.location")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {overview.accessPoints
                              .slice(
                                page * rowsPerPage,
                                page * rowsPerPage + rowsPerPage
                              )
                              .map((ap) => (
                                <TableRow key={ap.name} hover>
                                  <TableCell>
                                    {formatName(ap.name, t)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {integer.format(ap.views)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {currency.format(ap.netEarnings)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {ap.liveConnections}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      size="small"
                                      startIcon={<LocationOnOutlined />}
                                      aria-label={t("dashboard.viewOnMap", {
                                        name: ap.name,
                                      })}
                                      disabled={
                                        ap.latitude == null ||
                                        ap.longitude == null
                                      }
                                      onClick={() =>
                                        setMapCoordinates({
                                          name: ap.name,
                                          longitude: ap.longitude,
                                          latitude: ap.latitude,
                                        })
                                      }
                                      style={{
                                        borderRadius: 10,
                                        textTransform: "none",
                                      }}
                                    >
                                      {t("dashboard.map")}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                        <TablePagination
                          component="div"
                          count={overview.accessPoints.length}
                          page={page}
                          rowsPerPage={rowsPerPage}
                          onPageChange={(_, value) => setPage(value)}
                          onRowsPerPageChange={(event) => {
                            setRowsPerPage(Number(event.target.value));
                            setPage(0);
                          }}
                          labelRowsPerPage={t("dashboard.rowsPerPage")}
                          labelDisplayedRows={({ from, to, count }) =>
                            t("dashboard.rowsDisplayed", { from, to, count })
                          }
                          rowsPerPageOptions={[5, 10, 25]}
                        />
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Surface>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Surface>
                <CardContent>
                  <SectionHeading
                    eyebrow={t("dashboard.ranking")}
                    title={t("dashboard.topAccessPoints")}
                    description={t("dashboard.topAccessPointsDescription")}
                  />
                  {overview.accessPoints.length === 0 ? (
                    <EmptyState>{t("dashboard.noAccessPointViews")}</EmptyState>
                  ) : (
                    <Box height={360}>
                      <Bar
                        data={apChartData}
                        options={{
                          maintainAspectRatio: false,
                          legend: { display: false },
                          scales: {
                            yAxes: [
                              {
                                ticks: {
                                  beginAtZero: true,
                                  callback: (value) => currency.format(value),
                                },
                              },
                            ],
                          },
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Surface>
            </Grid>
          </Grid>

          <CompactSurface style={{ marginTop: 24 }}>
            <CardContent style={{ padding: 20 }}>
              <SectionHeading
                eyebrow={t("dashboard.suggestedActions")}
                title={t("dashboard.recommendations")}
                description={t("dashboard.recommendationsDescription")}
              />
              <RecommendationsViewport>
                <Grid container spacing={2}>
                  {overview.recommendations.map((item) => (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      key={`${item.type}-${item.title}`}
                      style={{ display: "flex" }}
                    >
                      <Paper
                        variant="outlined"
                        style={{
                          padding: 14,
                          width: "100%",
                          borderLeft: `4px solid ${
                            item.severity === "high"
                              ? "#dc2626"
                              : item.severity === "medium"
                              ? "#f59e0b"
                              : "#10b981"
                          }`,
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box display="flex">
                            <WarningOutlined
                              style={{ marginRight: 10, color: "#64748b" }}
                            />
                            <Box>
                              <Typography variant="h6">
                                {t(
                                  `recommendations.${item.type}.title`,
                                  item.title
                                )}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {t(
                                  `recommendations.${item.type}.reason`,
                                  item.reason
                                )}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            size="small"
                            label={
                              item.severity === "high"
                                ? t("dashboard.severityHigh")
                                : item.severity === "medium"
                                ? t("dashboard.severityMedium")
                                : t("dashboard.severityInfo")
                            }
                          />
                        </Box>
                        <Typography variant="body2" style={{ marginTop: 12 }}>
                          <strong>{t("dashboard.evidence")}:</strong>{" "}
                          {item.evidence}
                        </Typography>
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => history.push(item.actionUrl)}
                          style={{ marginTop: 8 }}
                        >
                          {t("dashboard.review")}
                        </Button>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </RecommendationsViewport>
            </CardContent>
          </CompactSurface>

          <CompactSurface style={{ marginTop: 20, marginBottom: 8 }}>
            <CardContent style={{ padding: 20 }}>
              <SectionHeading
                eyebrow={t("dashboard.transparency")}
                title={t("dashboard.financialBreakdown")}
                description={t("dashboard.financialBreakdownDescription")}
              />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} md={3}>
                  <Typography color="textSecondary">
                    {t("dashboard.grossEarnings")}
                  </Typography>
                  <Typography variant="h6">
                    {currency.format(overview.totals.grossEarnings)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography color="textSecondary">
                    {t("dashboard.taxes")}
                  </Typography>
                  <Typography variant="h6">
                    − {currency.format(overview.totals.tax)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography color="textSecondary">
                    {t("dashboard.internalRetention")}
                  </Typography>
                  <Typography variant="h6">
                    − {currency.format(overview.totals.retention)}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography color="textSecondary">
                    {t("dashboard.payableNet")}
                  </Typography>
                  <Typography variant="h6" style={{ color: "#047857" }}>
                    {currency.format(overview.totals.netEarnings)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </CompactSurface>
        </>
      )}

      <Dialog
        open={Boolean(mapCoordinates)}
        onClose={() => setMapCoordinates(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 24,
            overflow: "hidden",
            background: "#f4f7fb",
          },
        }}
      >
        <MapDialogHeader>
          <Typography
            variant="overline"
            style={{ opacity: 0.82, fontWeight: 700 }}
          >
            {t("dashboard.deviceLocation")}
          </Typography>
          <Typography variant="h5" style={{ fontWeight: 800 }}>
            {t("dashboard.georeferencedView")}
          </Typography>
          <Typography variant="body2" style={{ opacity: 0.92 }}>
            {t("dashboard.mapDescription")}
          </Typography>
        </MapDialogHeader>
        <DialogContent style={{ padding: 0, background: "#dff3fb" }}>
          {mapCoordinates && <MapBoxMap coordinates={mapCoordinates} />}
        </DialogContent>
        <MapDialogFooter>
          <Button
            onClick={() => setMapCoordinates(null)}
            color="primary"
            variant="contained"
            style={{ borderRadius: 8, textTransform: "none" }}
          >
            {t("common.close")}
          </Button>
        </MapDialogFooter>
      </Dialog>
    </>
  );
}

export default DashboardSummary;
